/**
 * Instance Catalog — slotting-core
 * Complete catalog of EC2 instance families and sizes for Gen1 and Gen2 Outposts racks.
 * Ported 1:1 from OutpostForge v1 js/catalog.js.
 *
 * In connected mode this catalog complements the live API
 * (GetOutpostSupportedInstanceTypes only exposes MaxVcpus, not memory per asset).
 * Extend by adding new families/sizes to INSTANCE_CATALOG.
 */

import type { InstanceCatalog, NumaConfig } from './types.js';

export const INSTANCE_CATALOG: InstanceCatalog = {
  gen1: {
    M5: {
      category: 'General Purpose',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 384, gpus: 0 },
      sizes: [
        { name: 'm5.large', vCPUs: 2, memoryGiB: 8 },
        { name: 'm5.xlarge', vCPUs: 4, memoryGiB: 16 },
        { name: 'm5.2xlarge', vCPUs: 8, memoryGiB: 32 },
        { name: 'm5.4xlarge', vCPUs: 16, memoryGiB: 64 },
        { name: 'm5.8xlarge', vCPUs: 32, memoryGiB: 128 },
        { name: 'm5.12xlarge', vCPUs: 48, memoryGiB: 192 },
        { name: 'm5.16xlarge', vCPUs: 64, memoryGiB: 256 },
        { name: 'm5.24xlarge', vCPUs: 96, memoryGiB: 384 },
      ],
    },
    M5d: {
      category: 'General Purpose (NVMe)',
      homogeneousOnly: true,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 384, gpus: 0 },
      sizes: [
        { name: 'm5d.large', vCPUs: 2, memoryGiB: 8 },
        { name: 'm5d.xlarge', vCPUs: 4, memoryGiB: 16 },
        { name: 'm5d.2xlarge', vCPUs: 8, memoryGiB: 32 },
        { name: 'm5d.4xlarge', vCPUs: 16, memoryGiB: 64 },
        { name: 'm5d.8xlarge', vCPUs: 32, memoryGiB: 128 },
        { name: 'm5d.12xlarge', vCPUs: 48, memoryGiB: 192 },
        { name: 'm5d.16xlarge', vCPUs: 64, memoryGiB: 256 },
        { name: 'm5d.24xlarge', vCPUs: 96, memoryGiB: 384 },
      ],
    },
    C5: {
      category: 'Compute Optimized',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 192, gpus: 0 },
      sizes: [
        { name: 'c5.large', vCPUs: 2, memoryGiB: 4 },
        { name: 'c5.xlarge', vCPUs: 4, memoryGiB: 8 },
        { name: 'c5.2xlarge', vCPUs: 8, memoryGiB: 16 },
        { name: 'c5.4xlarge', vCPUs: 16, memoryGiB: 32 },
        { name: 'c5.9xlarge', vCPUs: 36, memoryGiB: 72 },
        { name: 'c5.18xlarge', vCPUs: 72, memoryGiB: 144 },
      ],
    },
    C5d: {
      category: 'Compute Optimized (NVMe)',
      homogeneousOnly: true,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 192, gpus: 0 },
      sizes: [
        { name: 'c5d.large', vCPUs: 2, memoryGiB: 4 },
        { name: 'c5d.xlarge', vCPUs: 4, memoryGiB: 8 },
        { name: 'c5d.2xlarge', vCPUs: 8, memoryGiB: 16 },
        { name: 'c5d.4xlarge', vCPUs: 16, memoryGiB: 32 },
        { name: 'c5d.9xlarge', vCPUs: 36, memoryGiB: 72 },
        { name: 'c5d.18xlarge', vCPUs: 72, memoryGiB: 144 },
      ],
    },
    R5: {
      category: 'Memory Optimized',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 768, gpus: 0 },
      sizes: [
        { name: 'r5.large', vCPUs: 2, memoryGiB: 16 },
        { name: 'r5.xlarge', vCPUs: 4, memoryGiB: 32 },
        { name: 'r5.2xlarge', vCPUs: 8, memoryGiB: 64 },
        { name: 'r5.4xlarge', vCPUs: 16, memoryGiB: 128 },
        { name: 'r5.8xlarge', vCPUs: 32, memoryGiB: 256 },
        { name: 'r5.12xlarge', vCPUs: 48, memoryGiB: 384 },
        { name: 'r5.16xlarge', vCPUs: 64, memoryGiB: 512 },
        { name: 'r5.24xlarge', vCPUs: 96, memoryGiB: 768 },
      ],
    },
    R5d: {
      category: 'Memory Optimized (NVMe)',
      homogeneousOnly: true,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 768, gpus: 0 },
      sizes: [
        { name: 'r5d.large', vCPUs: 2, memoryGiB: 16 },
        { name: 'r5d.xlarge', vCPUs: 4, memoryGiB: 32 },
        { name: 'r5d.2xlarge', vCPUs: 8, memoryGiB: 64 },
        { name: 'r5d.4xlarge', vCPUs: 16, memoryGiB: 128 },
        { name: 'r5d.8xlarge', vCPUs: 32, memoryGiB: 256 },
        { name: 'r5d.12xlarge', vCPUs: 48, memoryGiB: 384 },
        { name: 'r5d.16xlarge', vCPUs: 64, memoryGiB: 512 },
        { name: 'r5d.24xlarge', vCPUs: 96, memoryGiB: 768 },
      ],
    },
    G4dn: {
      category: 'Accelerated Computing',
      homogeneousOnly: false,
      hasGPU: true,
      dropletCapacity: { vCPUs: 96, memoryGiB: 384, gpus: 4 },
      sizes: [
        { name: 'g4dn.xlarge', vCPUs: 4, memoryGiB: 16, gpus: 1 },
        { name: 'g4dn.2xlarge', vCPUs: 8, memoryGiB: 32, gpus: 1 },
        { name: 'g4dn.4xlarge', vCPUs: 16, memoryGiB: 64, gpus: 1 },
        { name: 'g4dn.8xlarge', vCPUs: 32, memoryGiB: 128, gpus: 1 },
        { name: 'g4dn.12xlarge', vCPUs: 48, memoryGiB: 192, gpus: 4 },
        { name: 'g4dn.16xlarge', vCPUs: 64, memoryGiB: 256, gpus: 1 },
      ],
    },
    I3en: {
      category: 'Storage Optimized',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 96, memoryGiB: 768, gpus: 0 },
      sizes: [
        { name: 'i3en.large', vCPUs: 2, memoryGiB: 16 },
        { name: 'i3en.xlarge', vCPUs: 4, memoryGiB: 32 },
        { name: 'i3en.2xlarge', vCPUs: 8, memoryGiB: 64 },
        { name: 'i3en.3xlarge', vCPUs: 12, memoryGiB: 96 },
        { name: 'i3en.6xlarge', vCPUs: 24, memoryGiB: 192 },
        { name: 'i3en.12xlarge', vCPUs: 48, memoryGiB: 384 },
        { name: 'i3en.24xlarge', vCPUs: 96, memoryGiB: 768 },
      ],
    },
  },
  gen2: {
    M7i: {
      category: 'General Purpose',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 192, memoryGiB: 768, gpus: 0 },
      sizes: [
        { name: 'm7i.large', vCPUs: 2, memoryGiB: 8 },
        { name: 'm7i.xlarge', vCPUs: 4, memoryGiB: 16 },
        { name: 'm7i.2xlarge', vCPUs: 8, memoryGiB: 32 },
        { name: 'm7i.4xlarge', vCPUs: 16, memoryGiB: 64 },
        { name: 'm7i.8xlarge', vCPUs: 32, memoryGiB: 128 },
        { name: 'm7i.12xlarge', vCPUs: 48, memoryGiB: 192 },
        { name: 'm7i.16xlarge', vCPUs: 64, memoryGiB: 256 },
        { name: 'm7i.24xlarge', vCPUs: 96, memoryGiB: 384 },
        { name: 'm7i.48xlarge', vCPUs: 192, memoryGiB: 768 },
      ],
    },
    C7i: {
      category: 'Compute Optimized',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 192, memoryGiB: 384, gpus: 0 },
      sizes: [
        { name: 'c7i.large', vCPUs: 2, memoryGiB: 4 },
        { name: 'c7i.xlarge', vCPUs: 4, memoryGiB: 8 },
        { name: 'c7i.2xlarge', vCPUs: 8, memoryGiB: 16 },
        { name: 'c7i.4xlarge', vCPUs: 16, memoryGiB: 32 },
        { name: 'c7i.8xlarge', vCPUs: 32, memoryGiB: 64 },
        { name: 'c7i.12xlarge', vCPUs: 48, memoryGiB: 96 },
        { name: 'c7i.16xlarge', vCPUs: 64, memoryGiB: 128 },
        { name: 'c7i.24xlarge', vCPUs: 96, memoryGiB: 192 },
        { name: 'c7i.48xlarge', vCPUs: 192, memoryGiB: 384 },
      ],
    },
    R7i: {
      category: 'Memory Optimized',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 192, memoryGiB: 1536, gpus: 0 },
      sizes: [
        { name: 'r7i.large', vCPUs: 2, memoryGiB: 16 },
        { name: 'r7i.xlarge', vCPUs: 4, memoryGiB: 32 },
        { name: 'r7i.2xlarge', vCPUs: 8, memoryGiB: 64 },
        { name: 'r7i.4xlarge', vCPUs: 16, memoryGiB: 128 },
        { name: 'r7i.8xlarge', vCPUs: 32, memoryGiB: 256 },
        { name: 'r7i.12xlarge', vCPUs: 48, memoryGiB: 384 },
        { name: 'r7i.16xlarge', vCPUs: 64, memoryGiB: 512 },
        { name: 'r7i.24xlarge', vCPUs: 96, memoryGiB: 768 },
        { name: 'r7i.48xlarge', vCPUs: 192, memoryGiB: 1536 },
      ],
    },
    'Bmn-sf2e': {
      category: 'Accelerated Networking',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 192, memoryGiB: 768, gpus: 0 },
      sizes: [
        { name: 'bmn-sf2e.xlarge', vCPUs: 4, memoryGiB: 16 },
        { name: 'bmn-sf2e.2xlarge', vCPUs: 8, memoryGiB: 32 },
        { name: 'bmn-sf2e.4xlarge', vCPUs: 16, memoryGiB: 64 },
        { name: 'bmn-sf2e.12xlarge', vCPUs: 48, memoryGiB: 192 },
        { name: 'bmn-sf2e.24xlarge', vCPUs: 96, memoryGiB: 384 },
        { name: 'bmn-sf2e.48xlarge', vCPUs: 192, memoryGiB: 768 },
      ],
    },
    'Bmn-cx2': {
      category: 'Accelerated Networking',
      homogeneousOnly: false,
      hasGPU: false,
      dropletCapacity: { vCPUs: 192, memoryGiB: 384, gpus: 0 },
      sizes: [
        { name: 'bmn-cx2.xlarge', vCPUs: 4, memoryGiB: 8 },
        { name: 'bmn-cx2.2xlarge', vCPUs: 8, memoryGiB: 16 },
        { name: 'bmn-cx2.4xlarge', vCPUs: 16, memoryGiB: 32 },
        { name: 'bmn-cx2.12xlarge', vCPUs: 48, memoryGiB: 96 },
        { name: 'bmn-cx2.24xlarge', vCPUs: 96, memoryGiB: 192 },
        { name: 'bmn-cx2.48xlarge', vCPUs: 192, memoryGiB: 384 },
      ],
    },
  },
};

/**
 * NUMA topology per generation.
 * Keys match INSTANCE_CATALOG generation keys.
 * Generations absent from this map have no NUMA validation.
 */
export const NUMA_CONFIG: Record<string, NumaConfig> = {
  gen1: { nodeCount: 2, vCPUsPerNode: 48 },
  gen2: { nodeCount: 2, vCPUsPerNode: 96 },
};

/**
 * One color per size index within a family (large → 48xlarge, 9 max).
 * Ordered as a cool→warm progression so bigger slots read warmer. The two
 * darkest values sit on the smallest sizes; the in-use band (black overlay
 * in visualizer.css) carries a light hairline so it stays visible on them.
 */
export const INSTANCE_COLORS: string[] = [
  '#264654', // large — dark petrol
  '#1c3457', // xlarge — dark navy
  '#467c9e', // 2xlarge — steel blue
  '#289e8f', // 4xlarge — teal green
  '#3cada5', // 8xlarge — teal
  '#77c7b8', // 12xlarge — mint
  '#e8c369', // 16xlarge — amber
  '#f3a361', // 24xlarge — light orange
  '#e76f51', // 48xlarge — coral
];
